import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Flame, CalendarCheck, AlertTriangle, CalendarPlus, FileClock, Wallet, Repeat } from 'lucide-react';
import { sek } from './crmUtils';

const CARDS = [
  { key: 'hot', label: 'Heta leads', icon: Flame, accent: 'from-orange-50 to-white dark:from-orange-950 dark:to-slate-800' },
  { key: 'today', label: 'Uppföljningar idag', icon: CalendarCheck, accent: 'from-amber-50 to-white dark:from-amber-950 dark:to-slate-800' },
  { key: 'overdue', label: 'Försenade uppföljningar', icon: AlertTriangle, accent: 'from-red-50 to-white dark:from-red-950 dark:to-slate-800' },
  { key: 'meetings_to_book', label: 'Möten att boka', icon: CalendarPlus, accent: 'from-violet-50 to-white dark:from-violet-950 dark:to-slate-800' },
  { key: 'quotes_to_follow', label: 'Offerter att följa upp', icon: FileClock, accent: 'from-blue-50 to-white dark:from-blue-950 dark:to-slate-800' },
];

function Card({ value, label, sub, icon: Icon, accent, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`relative overflow-hidden rounded-xl border bg-gradient-to-br ${accent} p-4 text-left transition-shadow hover:shadow-md`}>
      <Icon className="absolute -right-2 -top-2 h-16 w-16 opacity-[0.07]" strokeWidth={1.5} />
      <div className="text-2xl md:text-3xl font-extrabold tracking-tight">{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </button>
  );
}

export function TodaySales({ crm, leads, onOpenLead, onReminder }) {
  const [open, setOpen] = useState(null);
  if (!crm) return null;
  const k = crm.kpis;
  const list = open ? leads.filter(l => (open.key === 'value' ? ['new', 'contacted', 'demo', 'quote_sent', 'negotiation'].includes(l.stage) : (k[open.key] || []).includes(l.id))) : [];
  return (
    <section data-testid="today-sales">
      <h2 className="text-sm font-semibold mb-2">Dagens försäljning</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        {CARDS.map(c => <Card key={c.key} {...c} value={(k[c.key] || []).length} onClick={() => setOpen(c)} testId={`today-kpi-${c.key}`} />)}
        <Card value={sek(k.potential_value)} label="Potentiellt affärsvärde" sub={`Viktat ${sek(k.weighted_value)}`} icon={Wallet}
          accent="from-emerald-50 to-white dark:from-emerald-950 dark:to-slate-800" onClick={() => setOpen({ key: 'value', label: 'Aktiva affärer' })} testId="today-kpi-value" />
        <Card value={`${sek(k.potential_license)}/år`} label="Potentiell årlig licensintäkt" sub={`Viktat ${sek(k.weighted_license)}/år`} icon={Repeat}
          accent="from-teal-50 to-white dark:from-teal-950 dark:to-slate-800" onClick={() => setOpen({ key: 'value', label: 'Aktiva affärer' })} testId="today-kpi-license" />
      </div>
      <Dialog open={!!open} onOpenChange={v => !v && setOpen(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="today-list-dialog">
          <DialogHeader><DialogTitle>{open?.label}</DialogTitle></DialogHeader>
          {list.length === 0 && <p className="text-sm text-muted-foreground">Inga leads just nu.</p>}
          <div className="space-y-2">
            {list.map(l => (
              <div key={l.id} className="rounded-lg border p-3 text-sm" data-testid={`today-item-${l.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <button className="font-semibold text-left hover:underline" onClick={() => { setOpen(null); onOpenLead(l); }}>{l.school_name}</button>
                  <span className="text-xs text-muted-foreground">{sek(l.crm.value.first_year)} · {l.crm.probability}%</span>
                </div>
                {l.contact_name && <div className="text-xs text-muted-foreground">{l.contact_name}{l.contact_phone ? ` · ${l.contact_phone}` : ''}</div>}
                {l.crm.reminders.map(r => (
                  <div key={r.key} className="mt-2 flex flex-wrap items-center gap-2 rounded-md bg-muted/50 px-2 py-1.5 text-xs">
                    <span className="flex-1"><b>{r.title}</b> – {r.reason}</span>
                    <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => onReminder(l.id, r.key, 'done')} data-testid={`rem-done-${l.id}`}>Klar</Button>
                    <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => onReminder(l.id, r.key, 'snooze', 1)}>+1 dag</Button>
                    <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => onReminder(l.id, r.key, 'snooze', 7)}>+1 vecka</Button>
                  </div>
                ))}
                {l.crm.priority.reasons.length > 0 && <div className="mt-1 text-[11px] text-muted-foreground">Prioritet {l.crm.priority.label}: {l.crm.priority.reasons.join(', ')}</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
